import React, { useMemo } from 'react';
import { Button, Input, DatePicker, Space, Popconfirm, Table, Tag, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

export interface CreateBusinessDirectorData {
  id?: number;
  business_id?: number;
  customer_id?: number;
  name?: string;
  identification_no?: string;
  appoint_date?: string;
  resign_date?: string;
  position?: string;
  _isDeletedLocal?: boolean;
  _isNewFromRequest?: boolean;
  _addRequest?: any;
  _deleteRequest?: any;
  _crIndex?: number;
  _actualIndex?: number;
}

interface DirectorsTabProps {
  directors: CreateBusinessDirectorData[];
  addDirector: () => void;
  removeDirector: (index: number) => void;
  updateDirector: (index: number, field: keyof CreateBusinessDirectorData, value: string | number | undefined) => void;
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  deletedDirectors?: Array<{ index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  getDirectorDisplayValue?: (index: number, field: keyof CreateBusinessDirectorData) => string;
  handleDirectorFieldChange?: (index: number, field: keyof CreateBusinessDirectorData, value: string | number | undefined) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveDirectorDelete?: (requestId: number) => void;
  handleRejectDirectorDelete?: (requestId: number) => void;
  customers?: Array<{ id: number; name: string; ic_passport_no?: string }>;
}

export const BusinessDirectorsTab: React.FC<DirectorsTabProps> = ({
  directors,
  addDirector,
  updateDirector,
  removeDirector,
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  deletedDirectors = [],
  changeRequests = [],
  getDirectorDisplayValue,
  handleDirectorFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveDirectorDelete,
  handleRejectDirectorDelete,
  customers = [],
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;
  const navigate = useNavigate();

  const getFieldChangeRequest = (index: number, field: keyof CreateBusinessDirectorData) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `director_${index}_${field}`);

  const getDeleteChangeRequest = (index: number) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `director_${index}_delete`);

  const processedDirectors = useMemo(() => {
    const deletedKeys = new Set<string>();
    deletedDirectors.forEach((d) => {
      if (d.data) {
        const key = `${d.data.id || ''}_${d.data.customer_id || ''}_${d.data.identification_no || ''}`;
        deletedKeys.add(key);
      }
    });

    const all: any[] = [];
    directors.forEach((dir, currentIdx) => {
      const key = `${dir.id || ''}_${dir.customer_id || ''}_${dir.identification_no || ''}`;
      if (!deletedKeys.has(key)) {
        let originalIdx = currentIdx;
        deletedDirectors.forEach((d) => {
          if (d.index <= currentIdx) originalIdx++;
        });
        all.push({ ...dir, _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    all.sort((a, b) => a._crIndex - b._crIndex);

    const deletedSorted = [...deletedDirectors].sort((a, b) => b.index - a.index);
    deletedSorted.forEach((d) => {
      if (d.data) {
        const key = `${d.data.id || ''}_${d.data.customer_id || ''}_${d.data.identification_no || ''}`;
        const exists = all.some((dir) => `${dir.id || ''}_${dir.customer_id || ''}_${dir.identification_no || ''}` === key);
        if (!exists) {
          all.splice(d.index, 0, { ...d.data, _crIndex: d.index, _isDeletedLocal: true });
        }
      }
    });

    // Track add request index for proper ordering
    let addRequestIndex = 0;
    changeRequests.forEach((cr) => {
      if (cr.status === 'pending' && cr.field_name === 'director_add') {
        // Handle director_add format: contains a single director object (not an array)
        try {
          const dir = JSON.parse(cr.new_value || '{}');
          const key = `${dir.id || ''}_${dir.name || ''}_${dir.identification_no || ''}`;
          const exists = all.some((d) => `${d.id || ''}_${d.name || ''}_${d.identification_no || ''}` === key);
          if (!exists) {
            all.push({ ...dir, _isNewFromRequest: true, _addRequest: cr, _crIndex: 999 + addRequestIndex });
            addRequestIndex++;
          }
        } catch (_) {}
      } else if (cr.status === 'pending' && cr.field_name.startsWith('director_') && cr.field_name.endsWith('_add')) {
        const parts = cr.field_name.split('_');
        if (parts.length >= 3) {
          const index = parseInt(parts[1], 10);
          try {
            const dir = JSON.parse(cr.new_value || '{}');
            const key = `${dir.id || ''}_${dir.customer_id || ''}_${dir.identification_no || ''}`;
            const exists = all.some((d) => `${d.id || ''}_${d.customer_id || ''}_${d.identification_no || ''}` === key);
            if (!exists) {
              all.push({ ...dir, _isNewFromRequest: true, _addRequest: cr, _crIndex: index });
            }
          } catch (_) {}
        }
      }
    });

    const data = all.map((dir, idx) => {
      const crIdx = dir._crIndex !== undefined ? dir._crIndex : idx;
      const actualIdx = dir._actualIndex !== undefined ? dir._actualIndex : idx;
      const isDeleted =
        dir._isDeletedLocal ||
        deletedDirectors.some((d) => d.index === crIdx) ||
        getDeleteChangeRequest(crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(crIdx);
      return {
        ...dir,
        key: `director-${crIdx}-${idx}-${dir.id || 'new'}-${dir._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: actualIdx,
        isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: dir._isNewFromRequest || false,
        addRequest: dir._addRequest,
      };
    });

    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 1;
      if (!record.id) return 2;
      return 0;
    };

    data.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      return a.originalIndex - b.originalIndex;
    });

    return data;
  }, [directors, deletedDirectors, changeRequests]);

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: '20%',
      render: (customerId: number, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'customer_id');
        
        if (canEdit && !record.isDeleted) {
          let displayValue = customerId || undefined;
          if (cr?.new_value) displayValue = parseInt(cr.new_value, 10);
          else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const d = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = d?.customer_id || displayValue;
            } catch {
              displayValue = customerId || undefined;
            }
          } else if (getDirectorDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getDirectorDisplayValue(idx, 'customer_id');
            if (pending !== '') displayValue = parseInt(pending, 10);
          }
          
  return (
            <Select
              placeholder="Select Customer"
              allowClear
              showSearch
              optionFilterProp="children"
              value={displayValue}
              onChange={(value) => {
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleDirectorFieldChange) {
                  handleDirectorFieldChange(idx, 'customer_id', value);
                  // Auto-fill identification_no when customer is selected
                  if (value) {
                    const selectedCustomer = customers.find(c => c.id === value);
                    if (selectedCustomer?.ic_passport_no) {
                      handleDirectorFieldChange(idx, 'identification_no', selectedCustomer.ic_passport_no);
                    }
                  }
                } else if (updateDirector) {
                  updateDirector(idx, 'customer_id', value);
                  // Auto-fill identification_no when customer is selected
                  if (value) {
                    const selectedCustomer = customers.find(c => c.id === value);
                    if (selectedCustomer?.ic_passport_no) {
                      updateDirector(idx, 'identification_no', selectedCustomer.ic_passport_no);
                    }
                  }
                }
              }}
              style={{ width: '100%' }}
            >
              {customers.map((customer) => (
                <Select.Option key={customer.id} value={customer.id}>
                  {customer.name}
                </Select.Option>
              ))}
            </Select>
          );
        }
        
        if (cr) {
          if (cr.status === 'approved') {
            const customer = customers.find(c => c.id === parseInt(cr.new_value || '0', 10));
            return <span>{customer?.name || cr.new_value || '-'}</span>;
          }
          if (cr.status === 'rejected') {
            const customer = customers.find(c => c.id === parseInt(cr.old_value || '0', 10));
            return <span>{customer?.name || cr.old_value || '-'}</span>;
          }
          const oldCustomer = customers.find(c => c.id === parseInt(cr.old_value || '0', 10));
          const newCustomer = customers.find(c => c.id === parseInt(cr.new_value || '0', 10));
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {oldCustomer?.name || cr.old_value || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {newCustomer?.name || cr.new_value || '-'}
              </span>
            </div>
          );
        }
        
        const customer = customers.find(c => c.id === customerId);
        if (!customerId) {
          return <span style={cellStyle}>{customer?.name || '-'}</span>;
        }
        const basePath = isCustomerPortal ? '/customer' : '';
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => navigate(`${basePath}/customers/${customerId}/edit`)}
          >
            {customer?.name || '-'}
          </Button>
        );
      },
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: '16%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'position');
        if (canEdit && !record.isDeleted) {
          let displayValue = value || '';
          if (cr?.new_value) displayValue = cr.new_value;
          else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const d = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = d?.position || displayValue;
            } catch {
              displayValue = value || '';
            }
          } else if (getDirectorDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getDirectorDisplayValue(idx, 'position');
            if (pending !== '') displayValue = pending;
          }
          return (
                <Input
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleDirectorFieldChange) handleDirectorFieldChange(idx, 'position', val);
                else updateDirector(idx, 'position', val);
              }}
              placeholder="Position"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{cr.new_value || value || '-'}</span>;
          if (cr.status === 'rejected') return <span>{cr.old_value || value || '-'}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{cr.old_value || value || '-'}</span>
              <span style={{ color: '#ff9800' }}>{cr.new_value || '-'}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{value || '-'}</span>;
      },
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      width: '16%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'appoint_date');
        const formatDate = (val?: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-');
        if (canEdit && !record.isDeleted) {
          let displayValue = value ? dayjs(value) : null;
          if (cr?.new_value) displayValue = cr.new_value ? dayjs(cr.new_value) : null;
          return (
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={displayValue}
              onChange={(date) => {
                const val = date ? date.startOf('day').format('YYYY-MM-DD') : '';
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleDirectorFieldChange) handleDirectorFieldChange(idx, 'appoint_date', val);
                else updateDirector(idx, 'appoint_date', val);
              }}
              placeholder="Appoint date"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{formatDate(cr.new_value || value)}</span>;
          if (cr.status === 'rejected') return <span>{formatDate(cr.old_value || value)}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{formatDate(cr.old_value || value)}</span>
              <span style={{ color: '#ff9800' }}>{formatDate(cr.new_value)}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{formatDate(value)}</span>;
      },
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      width: '16%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'resign_date');
        const formatDate = (val?: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-');
        if (canEdit && !record.isDeleted) {
          let displayValue = value ? dayjs(value) : null;
          if (cr?.new_value) displayValue = cr.new_value ? dayjs(cr.new_value) : null;
          return (
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={displayValue}
              onChange={(date) => {
                const val = date ? date.startOf('day').format('YYYY-MM-DD') : '';
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleDirectorFieldChange) handleDirectorFieldChange(idx, 'resign_date', val);
                else updateDirector(idx, 'resign_date', val);
              }}
              placeholder="Resign date"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{formatDate(cr.new_value || value)}</span>;
          if (cr.status === 'rejected') return <span>{formatDate(cr.old_value || value)}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{formatDate(cr.old_value || value)}</span>
              <span style={{ color: '#ff9800' }}>{formatDate(cr.new_value)}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{formatDate(value)}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '8%',
      render: (_: any, record: any) => {
        if (record.isDeleted || record.deleteRequest) {
          const deleteReq = record.deleteRequest || changeRequests.find(
            (cr) => cr.field_name === `director_${record.originalIndex}_delete` && cr.status === 'pending'
          );
          if (deleteReq) {
            return <Tag color="orange">Pending</Tag>;
          }
        }

        if (record.isNewFromRequest && record.addRequest) {
          return <Tag color="orange">Pending</Tag>;
        }

        const fieldCRs = [
          getFieldChangeRequest(record.originalIndex, 'customer_id'),
          getFieldChangeRequest(record.originalIndex, 'identification_no'),
          getFieldChangeRequest(record.originalIndex, 'position'),
          getFieldChangeRequest(record.originalIndex, 'appoint_date'),
          getFieldChangeRequest(record.originalIndex, 'resign_date'),
        ].filter(Boolean);

        if (fieldCRs.length > 0) {
          return <Tag color="orange">Pending</Tag>;
        }

        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          const idx = record.actualIndex ?? record.originalIndex;
          return (
            <Popconfirm
              title="Are you sure you want to delete this director?"
              onConfirm={() => removeDirector(idx)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={processedDirectors}
        pagination={false}
        rowKey="key"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const hasPendingFieldChange = changeRequests.some(
            (cr) =>
              cr.status === 'pending' &&
              cr.field_name.startsWith(`director_${record.originalIndex}_`) &&
              !cr.field_name.endsWith('_add') &&
              !cr.field_name.endsWith('_delete') &&
              cr.field_name !== 'director_add'
          );
          return {
            style: {
              backgroundColor: isDeleted ? '#ffebee' : isNew ? '#e3f2fd' : hasPendingFieldChange ? '#fff3cd' : undefined,
            },
          };
        }}
      />
      {canEdit && processedDirectors.filter((d: any) => !d.isDeleted && !d.isNewFromRequest).length < 10 && (
        <div style={{ marginTop: 12 }}>
          <Button
            type="link"
            style={{ color: '#ff4d4f', padding: 0 }}
            icon={<PlusOutlined />}
            onClick={addDirector}
          >
            (max 10)
          </Button>
        </div>
      )}
    </div>
  );
};
